/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Component, JSXElement, onMount } from 'solid-js';
import { defineSolidPopup } from '../Popup/popup-wc';
import { BtnClasses } from '../../types/common';

export interface ConfirmPopupProps {
  show?: boolean;
  waiting?: boolean;
  title?: string;
  description?: string | JSXElement;
  hideClose?: boolean;
  primaryBtnLabel?: string;
  primaryBtnClass?: BtnClasses;
  secondaryBtnLabel?: string;
  secondaryBtnClass?: BtnClasses;
  /** Extra classes on the root `.reveal-overlay` (e.g. z-index utility). */
  overlayClass?: string;
  // Callback props for event handling
  onClose?(): void;
  onAction?(): void;
}

const ConfirmPopup: Component<ConfirmPopupProps> = (props) => {
  // Define the popup web component
  onMount(() => {
    defineSolidPopup();
  });

  // Reactive getters for props
  const getShow = () => props.show ?? false;
  const getWaiting = () => props.waiting ?? false;
  const getTitle = () => props.title ?? '';
  const getDescription = () => props.description;
  const getHideClose = () => props.hideClose ?? false;
  const getPrimaryBtnLabel = () => props.primaryBtnLabel ?? 'Submit';
  const getPrimaryBtnClass = () => props.primaryBtnClass ?? 'primary';
  const getSecondaryBtnLabel = () => props.secondaryBtnLabel ?? 'Cancel';
  const getSecondaryBtnClass = () =>
    props.secondaryBtnClass ?? 'link-secondary';
  const getOverlayClass = () => props.overlayClass;

  // Generate dynamic name based on title
  const getName = () => {
    const title = getTitle();
    return title
      ? `confirm-popup-${title.toLowerCase().split(' ').join('-')}`
      : 'confirm-popup';
  };

  const handleClose = () => {
    if (!getWaiting()) {
      props.onClose?.();
    }
  };

  const handleAction = () => {
    if (!getWaiting()) {
      props.onAction?.();
    }
  };

  // Set up event listeners when the popup is mounted
  const setupEventListeners = (element: HTMLElement) => {
    element.addEventListener('popup-close', handleClose);
    element.addEventListener('popup-primary-action', handleAction);
    element.addEventListener('popup-secondary-action', handleClose);
  };

  return (
    // @ts-expect-error - tc-popup is a web component defined at runtime
    <tc-popup
      ref={(el: HTMLElement) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (el) {
          setupEventListeners(el);
        }
      }}
      show={getShow()}
      heading={getTitle()}
      name={getName()}
      content={getDescription()}
      hide-close={getHideClose()}
      hide-footer={false}
      primary-btn-label={getPrimaryBtnLabel()}
      primary-btn-class={getPrimaryBtnClass()}
      primary-btn-disabled={getWaiting()}
      secondary-btn-label={getSecondaryBtnLabel()}
      secondary-btn-class={getSecondaryBtnClass()}
      secondary-btn-disabled={getWaiting()}
      overlay-class={getOverlayClass()}
    >
      {/* @ts-expect-error Does not exist on intrinsic elements */}
    </tc-popup>
  );
};

export default ConfirmPopup;
